import React, { useEffect, useState } from 'react';
import { message, Button, Form, Input, DatePicker, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import dayjs from 'dayjs';
import {
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    StatusBadge,
} from "../../components/ui";

const AllDiscounts = () => {
    const { t } = useTranslation();
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form] = Form.useForm();

    // Fetch discounts data
    const fetchDiscounts = async () => {
        try {
            const response = await axiosInstance.get('/discount');
            if (response.data.success) {
                setDiscounts(response.data.data);
            } else {
                message.error(t('common.loadError'));
            }
        } catch (error) {
            console.error("Error fetching discounts:", error);
            message.error(t('common.loadError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDiscounts();
    }, []);

    // Handle form submission to create a new discount
    const handleCreateDiscount = async (values) => {
        const payload = {
            title: values.title,
            percent: values.percent,
            start_at: values.start_at ? dayjs(values.start_at).format("YYYY-MM-DD") : null,
            end_at: values.end_at ? dayjs(values.end_at).format("YYYY-MM-DD") : null,
        };

        try {
            const response = await axiosInstance.post('/admin/discount', payload);
            if (response.data.success) {
                message.success(t('discounts.created'));
                form.resetFields(); // Clear form fields
                fetchDiscounts(); // Refresh discount list
            } else {
                message.error(t('discounts.createError'));
            }
        } catch (error) {
            console.error("Error creating discount:", error);
            message.error(t('discounts.createError'));
        }
    };

    const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : t('common.dash'));

    // Define table columns
    const columns = [
        {
            title: t('common.id'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
            render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
        },
        {
            title: t('discounts.name'),
            dataIndex: 'title',
            key: 'title',
            render: (title) => <span className="text-sm font-medium text-slate-800">{title}</span>,
        },
        {
            title: t('discounts.percent'),
            dataIndex: 'percent',
            key: 'percent',
            width: 130,
            render: (percent) => <StatusBadge tone="brand">{`${percent}%`}</StatusBadge>,
        },
        {
            title: t('discounts.startDate'),
            dataIndex: 'start_at',
            key: 'start_at',
            width: 150,
            render: (date) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{formatDate(date)}</span>
            ),
        },
        {
            title: t('discounts.endDate'),
            dataIndex: 'end_at',
            key: 'end_at',
            width: 150,
            render: (date) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{formatDate(date)}</span>
            ),
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-2">
                <span className="min-w-0 flex-1 text-sm font-semibold text-[#1D2C7B]">{record.title}</span>
                <StatusBadge tone="brand">{`${record.percent}%`}</StatusBadge>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <span className="tabular-nums">{formatDate(record.start_at)}</span>
                <span className="tabular-nums">{formatDate(record.end_at)}</span>
            </div>
        </div>
    );

    return (
        <PageShell>
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-2.5 md:gap-4">
                {/* Main content area */}
                <PageCard>
                    <PageToolbar title={t('discounts.title')} count={discounts.length} />

                    <DataTable
                        columns={columns}
                        dataSource={discounts}
                        loading={loading}
                        mobileCard={renderMobileCard}
                        scrollX={760}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            position: ['bottomCenter'],
                            className: 'px-4 pb-3',
                        }}
                    />
                </PageCard>

                {/* Permanently visible sidebar for creating a new discount */}
                <PageCard>
                    <PageToolbar title={t('discounts.create')} />

                    <div className="p-3 sm:p-4">
                        <Form form={form} layout="vertical" onFinish={handleCreateDiscount}>
                            <Form.Item
                                name="title"
                                label={t('common.name')}
                                rules={[{ required: true, message: t('common.required') }]}
                            >
                                <Input placeholder={t('discounts.namePlaceholder')} />
                            </Form.Item>

                            <Form.Item
                                name="percent"
                                label={t('discounts.percent')}
                                rules={[{ required: true, message: t('common.required') }]}
                            >
                                <InputNumber min={0} max={100} placeholder="%" className="w-full" />
                            </Form.Item>

                            <Form.Item name="start_at" label={t('discounts.startDate')}>
                                <DatePicker format="YYYY-MM-DD" className="w-full" />
                            </Form.Item>

                            <Form.Item name="end_at" label={t('discounts.endDate')}>
                                <DatePicker format="YYYY-MM-DD" className="w-full" />
                            </Form.Item>

                            <Form.Item className="mb-0">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    className="w-full !h-9 !rounded-lg !text-sm !font-semibold"
                                    style={{ backgroundColor: '#1D2C7B', borderColor: '#1D2C7B' }}
                                >
                                   {t('discounts.submit')}
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </PageCard>
            </div>
        </PageShell>
    );
};

export default AllDiscounts;
